import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck, faTimes, faClipboardCheck, faSave, faArrowLeft,
    faInfoCircle, faFileInvoice, faBoxes, faExclamationTriangle,
    faWarehouse, faPlus, faTrash, faChevronDown, faChevronUp,
    faFilePdf, faUpload
} from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../utils/Constants/URLS';
import PageHeader from '../utils/PageHeader';

// ── Helper: build a fresh inspection map for a PO's line items ───────────────
const buildInitInspection = (lineItems) => {
    const map = {};
    lineItems?.forEach(item => {
        map[item._id] = {
            inspectedQuantity: 0,
            approvedQuantity: 0,
            wrongQuantity: 0,
            damagedQuantity: 0,
            shortSupply: 0,
            rejectionReason: ''
        };
    });
    return map;
};

// ── Helper: build a blank transporter entry ──────────────────────────────────
const newTransporter = () => ({
    id: Date.now() + Math.random(),
    gstin: '',            // optional
    transporterName: '',
    invoiceNo: '',
    invoiceDate: '',
    taxableValue: '',
    gstSlab: '',
    integratedTax: '',
    centralTax: '',
    stateTax: '',
    totalInvoiceValue: '',
});

// ── Helper: build a fresh invoice object ─────────────────────────────────────
const newInvoice = (lineItems) => ({
    id: Date.now() + Math.random(),
    // Header fields — mapped to API field names
    invoiceNumber: '',        // → invoiceNumber
    invoiceDate: '',          // → invoiceDate
    senderGstin: '',          // → senderGstin  (optional)
    senderName: '',           // → senderName   (required)
    transporterDetails: [],   // → transporterDetails  (array of transporter sub-docs)
    receivedDate: new Date().toISOString().split('T')[0], // → receivedDate (also used as batchDate)
    invoiceDocument: null,    // → file field invoiceDoc_<index>  (File object)
    inspectionData: buildInitInspection(lineItems),
    collapsed: false
});

const MaterialInspection = () => {
    const { poid } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    const [po, setPo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [submitError, setSubmitError] = useState('');

    // Stores the created inspection documents returned by the API after submit
    // Each entry has _id, invoiceNumber, tallyPurchaseSyncPending, etc.
    const [createdInspections, setCreatedInspections] = useState([]);
    // Tracks which inspection IDs are currently being manually synced to Tally
    const [syncingTally, setSyncingTally] = useState({});

    // Each invoice carries its own metadata + per-line-item inspection data
    const [invoices, setInvoices] = useState([]);

    useEffect(() => {
        fetchPO();
        // eslint-disable-next-line
    }, [poid]);

    // ── Fetch PO by ID directly ───────────────────────────────────────────────
    const fetchPO = async () => {
        setLoading(true);
        setFetchError('');
        try {
            const res = await fetch(`${API_URL}purchase-orders/${poid}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await res.json();
            if (result.error) throw new Error(result.message || 'Failed to load purchase order');

            const matched = result.data;
            if (!matched) throw new Error(`Purchase Order not found (ID: ${poid})`);

            setPo(matched);
            setInvoices([newInvoice(matched.lineItems)]);
        } catch (err) {
            setFetchError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ── Invoice-level handlers ────────────────────────────────────────────────
    const addInvoice = () => {
        setInvoices(prev => [...prev, newInvoice(po?.lineItems)]);
    };

    const removeInvoice = (invoiceId) => {
        setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
    };

    const toggleCollapse = (invoiceId) => {
        setInvoices(prev =>
            prev.map(inv => inv.id === invoiceId ? { ...inv, collapsed: !inv.collapsed } : inv)
        );
    };

    const handleInvoiceMeta = (invoiceId, field, value) => {
        setInvoices(prev =>
            prev.map(inv => inv.id === invoiceId ? { ...inv, [field]: value } : inv)
        );
    };

    // Handle PDF file selection per invoice
    const handleFileChange = (invoiceId, file) => {
        setInvoices(prev =>
            prev.map(inv => inv.id === invoiceId ? { ...inv, invoiceDocument: file } : inv)
        );
    };

    // ── Transporter sub-row handlers ─────────────────────────────────────────
    const addTransporter = (invoiceId) => {
        setInvoices(prev => prev.map(inv =>
            inv.id !== invoiceId ? inv : {
                ...inv,
                transporterDetails: [...inv.transporterDetails, newTransporter()]
            }
        ));
    };

    const removeTransporter = (invoiceId, transporterId) => {
        setInvoices(prev => prev.map(inv =>
            inv.id !== invoiceId ? inv : {
                ...inv,
                transporterDetails: inv.transporterDetails.filter(t => t.id !== transporterId)
            }
        ));
    };

    const handleTransporterField = (invoiceId, transporterId, field, value) => {
        setInvoices(prev => prev.map(inv => {
            if (inv.id !== invoiceId) return inv;
            return {
                ...inv,
                transporterDetails: inv.transporterDetails.map(t => {
                    if (t.id !== transporterId) return t;
                    const updated = { ...t, [field]: value };
                    // When no GSTIN: totalInvoiceValue mirrors taxableValue automatically
                    if (!updated.gstin?.trim()) {
                        if (field === 'taxableValue') updated.totalInvoiceValue = value;
                    }
                    return updated;
                })
            };
        }));
    };

    // ── Per-invoice line-item inspection handlers ─────────────────────────────
    const handleFieldChange = (invoiceId, itemId, field, value) => {
        setInvoices(prev => prev.map(inv => {
            if (inv.id !== invoiceId) return inv;

            const current = inv.inspectionData[itemId] || {};
            let newObj = { ...current, [field]: value };

            const numValue = value === '' ? '' : parseInt(value);

            if (field === 'inspectedQuantity') {
                newObj.inspectedQuantity = numValue;
                newObj.approvedQuantity = numValue;
                newObj.wrongQuantity = 0;
                newObj.damagedQuantity = 0;
                newObj.shortSupply = 0;
                newObj.rejectionReason = '';

            } else if (field === 'approvedQuantity') {
                const inspQty = parseInt(current.inspectedQuantity) || 0;
                const safeAppQty = numValue === '' ? '' : Math.max(0, numValue || 0);
                newObj.approvedQuantity = safeAppQty;

                const leftover = Math.max(0, inspQty - (safeAppQty === '' ? 0 : safeAppQty));
                const prevRej = (parseInt(current.wrongQuantity) || 0) +
                    (parseInt(current.damagedQuantity) || 0) +
                    (parseInt(current.shortSupply) || 0);
                if (prevRej !== leftover) {
                    newObj.wrongQuantity = leftover;
                    newObj.damagedQuantity = 0;
                    newObj.shortSupply = 0;
                }
                if (leftover === 0) newObj.rejectionReason = '';

            } else if (['wrongQuantity', 'damagedQuantity', 'shortSupply'].includes(field)) {
                const inspQty = parseInt(current.inspectedQuantity) || 0;
                const safeValue = numValue === '' ? '' : Math.max(0, numValue || 0);
                newObj[field] = safeValue;

                const newRejTotal =
                    (field === 'wrongQuantity' ? (safeValue || 0) : parseInt(current.wrongQuantity) || 0) +
                    (field === 'damagedQuantity' ? (safeValue || 0) : parseInt(current.damagedQuantity) || 0) +
                    (field === 'shortSupply' ? (safeValue || 0) : parseInt(current.shortSupply) || 0);
                newObj.approvedQuantity = Math.max(0, inspQty - newRejTotal);
                if (newRejTotal === 0) newObj.rejectionReason = '';
            }

            return {
                ...inv,
                inspectionData: { ...inv.inspectionData, [itemId]: newObj }
            };
        }));
    };

    // ── Cross-invoice qty helper ──────────────────────────────────────────────
    const totalInspectedForItem = (itemId) =>
        invoices.reduce((sum, inv) => sum + (Number(inv.inspectionData[itemId]?.inspectedQuantity) || 0), 0);

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        setSubmitError('');
        setSubmitSuccess('');

        if (invoices.length === 0) {
            setSubmitError('Please add at least one invoice before submitting.');
            return;
        }

        // ── Client-side validation ────────────────────────────────────────────
        for (let i = 0; i < invoices.length; i++) {
            const inv = invoices[i];
            const label = `Invoice ${i + 1}`;

            if (!inv.invoiceNumber.trim()) {
                setSubmitError(`${label}: Invoice Number is required.`);
                return;
            }
            if (!inv.senderName.trim()) {
                setSubmitError(`${label}: Sender Name is required.`);
                return;
            }
            if (!inv.receivedDate) {
                setSubmitError(`${label}: Received / Batch Date is required.`);
                return;
            }
            if (inv.invoiceDocument && inv.invoiceDocument.type !== 'application/pdf') {
                setSubmitError(`${label}: Only PDF files are allowed for invoice documents.`);
                return;
            }

            // Transporter validation
            for (let k = 0; k < inv.transporterDetails.length; k++) {
                const tp = inv.transporterDetails[k];
                const tpLabel = `${label} / Transporter ${k + 1}`;

                if (!tp.transporterName?.trim()) {
                    setSubmitError(`${tpLabel}: Transporter Name is required.`);
                    return;
                }
                if (!tp.invoiceNo?.trim()) {
                    setSubmitError(`${tpLabel}: Invoice No is required.`);
                    return;
                }
                if (!tp.invoiceDate) {
                    setSubmitError(`${tpLabel}: Invoice Date is required.`);
                    return;
                }
                if (tp.taxableValue === '') {
                    setSubmitError(`${tpLabel}: Taxable Value is required.`);
                    return;
                }
                if (tp.totalInvoiceValue === '') {
                    setSubmitError(`${tpLabel}: Total Invoice Value is required.`);
                    return;
                }

                if (tp.gstin?.trim()) {
                    if (tp.gstSlab === '') {
                        setSubmitError(`${tpLabel}: GST Slab is required when GSTIN is present.`);
                        return;
                    }
                    const hasIgst = tp.integratedTax !== '' && Number(tp.integratedTax) > 0;
                    const hasCgst = tp.centralTax !== '' && Number(tp.centralTax) > 0;
                    const hasSgst = tp.stateTax !== '' && Number(tp.stateTax) > 0;

                    if (!hasIgst && !(hasCgst && hasSgst)) {
                        setSubmitError(`${tpLabel}: Please provide either IGST, or both CGST and SGST.`);
                        return;
                    }
                    if (hasIgst && (hasCgst || hasSgst)) {
                        setSubmitError(`${tpLabel}: Please provide either IGST or (CGST & SGST), but not both.`);
                        return;
                    }
                    if ((hasCgst && !hasSgst) || (!hasCgst && hasSgst)) {
                        setSubmitError(`${tpLabel}: Both CGST and SGST must be provided together.`);
                        return;
                    }
                }
            }

            for (let j = 0; j < (po?.lineItems || []).length; j++) {
                const item = po.lineItems[j];
                const data = inv.inspectionData[item._id] || {};
                const inspQty = Number(data.inspectedQuantity || 0);
                const appQty = Number(data.approvedQuantity || 0);
                const wrongQty = Number(data.wrongQuantity || 0);
                const damagedQty = Number(data.damagedQuantity || 0);
                const shortQty = Number(data.shortSupply || 0);
                const rejQty = wrongQty + damagedQty + shortQty;

                if (inspQty < 0) {
                    setSubmitError(`${label} / Line ${j + 1}: Inspected quantity cannot be negative.`);
                    return;
                }
                if (inspQty !== appQty + rejQty) {
                    setSubmitError(`${label} / Line ${j + 1}: Approved + (Wrong + Damaged + Short Supply) must equal Inspected quantity.`);
                    return;
                }
                if (rejQty > 0 && !data.rejectionReason?.trim()) {
                    setSubmitError(`${label} / Line ${j + 1}: Please provide a rejection reason.`);
                    return;
                }
            }
        }

        // Cross-invoice: total inspected must not exceed PO qty per line item
        for (const item of (po?.lineItems || [])) {
            const total = totalInspectedForItem(item._id);
            if (total > item.quantity) {
                setSubmitError(
                    `"${item.model?.modelName || item._id}": total inspected across all invoices (${total}) exceeds PO quantity (${item.quantity}).`
                );
                return;
            }
        }

        // ── Build invoiceDetails payload (JSON, to be stringified into FormData) ──
        const invoiceDetails = invoices.map(inv => ({
            invoiceNumber: inv.invoiceNumber.trim(),
            invoiceDate: inv.invoiceDate || null,
            senderGstin: inv.senderGstin?.trim().toUpperCase() || null,
            senderName: inv.senderName.trim(),
            transporterDetails: inv.transporterDetails
                .filter(t => t.transporterName?.trim() || t.invoiceNo?.trim())
                .map(t => ({
                    gstin: t.gstin?.trim() || null,
                    transporterName: t.transporterName?.trim(),
                    invoiceNo: t.invoiceNo?.trim(),
                    invoiceDate: t.invoiceDate || null,
                    taxableValue: t.taxableValue !== '' ? Number(t.taxableValue) : 0,
                    gstSlab: t.gstin?.trim() && t.gstSlab !== '' ? Number(t.gstSlab) : null,
                    integratedTax: t.gstin?.trim() && t.integratedTax !== '' ? Number(t.integratedTax) : null,
                    centralTax: t.gstin?.trim() && t.centralTax !== '' ? Number(t.centralTax) : null,
                    stateTax: t.gstin?.trim() && t.stateTax !== '' ? Number(t.stateTax) : null,
                    totalInvoiceValue: t.totalInvoiceValue !== '' ? Number(t.totalInvoiceValue) : (t.taxableValue !== '' ? Number(t.taxableValue) : 0),
                })),
            receivedDate: inv.receivedDate,
            lineItems: (po?.lineItems || [])
                .filter(item => Number(inv.inspectionData[item._id]?.inspectedQuantity || 0) > 0)
                .map(item => {
                    const data = inv.inspectionData[item._id] || {};
                    const inspectedQty = Number(data.inspectedQuantity || 0);
                    const approvedQty = Number(data.approvedQuantity || 0);
                    const wrongQty = Number(data.wrongQuantity || 0);
                    const damagedQty = Number(data.damagedQuantity || 0);
                    const shortQty = Number(data.shortSupply || 0);
                    const rejQty = wrongQty + damagedQty + shortQty;
                    const entry = {
                        poLineItemId: item._id,
                        inspectedQuantity: inspectedQty,
                        approvedQuantity: approvedQty,
                        wrongQuantity: wrongQty,
                        damagedQuantity: damagedQty,
                        shortSupply: shortQty,
                        batchDate: inv.receivedDate
                    };
                    if (rejQty > 0) entry.rejectionReason = data.rejectionReason || '';
                    return entry;
                })
        }));


        const formData = new FormData();
        formData.append('purchaseOrder', poid);
        formData.append('invoiceDetails', JSON.stringify(invoiceDetails));

        invoices.forEach((inv, idx) => {
            if (inv.invoiceDocument) {
                formData.append(`invoiceDoc_${idx}`, inv.invoiceDocument);
            }
        });

        const hasRejections = invoices.some(inv =>
            Object.values(inv.inspectionData).some(d => (Number(d.rejectedQuantity) || 0) > 0)
        );

        setSubmitting(true);
        try {
            // NOTE: Do NOT set Content-Type header — browser sets it automatically
            // with the correct multipart boundary when using FormData
            const res = await fetch(`${API_URL}material-inspections`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`
                },
                body: formData
            });
            const result = await res.json();
            if (result.error) throw new Error(result.message || 'Inspection submission failed');

            const inspections = result.data?.inspections || [];
            setCreatedInspections(inspections);

            // Check if any purchase invoice failed to auto-sync to Tally
            const anyPending = inspections.some(insp => insp.tallyPurchaseSyncPending === true);

            setSubmitSuccess(
                hasRejections
                    ? 'Inspection saved! Batches created for all deliveries — rejected quantities marked in store.'
                    : 'Inspection saved! Inventory batches created for all approved deliveries.'
            );

            // Only auto-navigate if all syncs succeeded; otherwise let the user
            // see the Tally sync status and retry manually if needed
            if (!anyPending) {
                setTimeout(() => navigate('/purchase/view_po'), 2500);
            }
        } catch (err) {
            setSubmitError(err.message || 'Failed to submit inspection. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const hasAnyRejections = invoices.some(inv =>
        Object.values(inv.inspectionData).some(d =>
            ((Number(d.wrongQuantity) || 0) + (Number(d.damagedQuantity) || 0) + (Number(d.shortSupply) || 0)) > 0
        )
    );

    // ── Manual Tally sync handler ──────────────────────────────────────────────
    const handleSyncToTally = async (inspectionId) => {
        setSyncingTally(prev => ({ ...prev, [inspectionId]: true }));
        try {
            const res = await fetch(`${API_URL}purchase-invoices/sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ inspectionId })
            });
            const result = await res.json();
            if (result.error) throw new Error(result.message || 'Tally sync failed');

            // Mark this inspection as synced in local state
            setCreatedInspections(prev =>
                prev.map(insp =>
                    insp._id === inspectionId
                        ? { ...insp, tallyPurchaseSyncPending: false }
                        : insp
                )
            );
        } catch (err) {
            alert(`Tally Sync Failed: ${err.message}`);
        } finally {
            setSyncingTally(prev => ({ ...prev, [inspectionId]: false }));
        }
    };

    // ── Loading / error ───────────────────────────────────────────────────────
    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh] gap-3">
            <Spinner color="primary" />
            <span className="text-gray-600 font-semibold">Loading Purchase Order…</span>
        </div>
    );

    if (fetchError) return (
        <div className="p-10">
            <Alert color="danger">
                <strong>Error:</strong> {fetchError}
                <button className="btn btn-sm btn-outline-danger ml-4" onClick={fetchPO}>Retry</button>
            </Alert>
        </div>
    );

    // ── Main UI ───────────────────────────────────────────────────────────────
    return (
        <div>
            <PageHeader
                pageName={`INSPECT MATERIALS — ${po?.poNumber}`}
                breadcrump1="Purchase Orders"
                breadcrump2="Material Inspection"
                link="/purchase/view_po"
            />

            <div className="p-6 lg:px-12">
                <button
                    onClick={() => navigate(-1)}
                    className="btn btn-light border font-bold mb-4 flex items-center gap-2"
                >
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>

                {/* Instruction callout */}
                <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-5 text-sm text-blue-800">
                    <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0 text-blue-500" />
                    <p className="mb-0">
                        Each supplier invoice represents a <strong>separate partial delivery</strong>. Add one invoice per
                        delivery, fill in its details, and enter the inspected quantities for that delivery's line items.
                        Total quantities across all invoices <strong>must not exceed the PO quantity</strong> per item.
                        Click <strong>Save Inspection Results</strong> when all deliveries are recorded.
                    </p>
                </div>

                {/* PO Summary + cross-invoice qty tracker */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-6 py-4 mb-6 flex flex-wrap gap-4 justify-between items-center">
                    <div>
                        <h5 className="font-extrabold text-gray-900 mb-1">
                            <FontAwesomeIcon icon={faClipboardCheck} className="text-blue-500 mr-2" />
                            Material Inspection
                        </h5>
                        <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-0">
                            PO: {po?.poNumber}&nbsp;|&nbsp;Vendor: {po?.vendor?.vendorName || po?.vendor?.companyName}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {po?.lineItems?.map(item => {
                            const total = totalInspectedForItem(item._id);
                            const over = total > item.quantity;
                            const done = total === item.quantity;
                            return (
                                <span
                                    key={item._id}
                                    className={`text-[11px] font-bold px-3 py-1 rounded-full border ${over ? 'bg-red-50 border-red-300 text-red-700'
                                        : done ? 'bg-green-50 border-green-300 text-green-700'
                                            : 'bg-gray-50 border-gray-200 text-gray-500'
                                        }`}
                                >
                                    {item.model?.modelName}: {total} / {item.quantity} {item.model?.uom}
                                    {over && ' ⚠ Over'}
                                </span>
                            );
                        })}
                    </div>
                </div>

                {/* ── Invoice blocks ─────────────────────────────────────────── */}
                <div className="flex flex-col gap-6 mb-6">
                    {invoices.map((inv, invIdx) => {
                        const invHasRejections = Object.values(inv.inspectionData).some(
                            d => ((Number(d.wrongQuantity) || 0) + (Number(d.damagedQuantity) || 0) + (Number(d.shortSupply) || 0)) > 0
                        );

                        return (
                            <div key={inv.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

                                {/* Invoice header bar */}
                                <div className="flex items-center justify-between bg-gray-900 px-5 py-3">
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon={faFileInvoice} className="text-yellow-400" />
                                        <span className="text-white font-extrabold uppercase tracking-widest text-sm">
                                            Invoice {invIdx + 1}
                                            {inv.invoiceNumber && (
                                                <span className="ml-2 text-yellow-300 font-mono normal-case tracking-normal">
                                                    — {inv.invoiceNumber}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {invoices.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeInvoice(inv.id)}
                                                className="flex items-center gap-1.5 text-xs font-bold text-red-300 hover:text-white hover:bg-red-700 px-3 py-1.5 rounded-lg transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faTrash} /> Remove
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => toggleCollapse(inv.id)}
                                            className="text-gray-400 hover:text-white px-2 py-1.5 rounded-lg transition-colors"
                                            title={inv.collapsed ? 'Expand' : 'Collapse'}
                                        >
                                            <FontAwesomeIcon icon={inv.collapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>
                                </div>

                                {!inv.collapsed && (
                                    <div className="p-5">

                                        {/* Invoice metadata fields */}
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-5">
                                            <div className="flex flex-wrap gap-3 items-start">

                                                {/* Invoice Number */}
                                                <div className="flex-1 min-w-[180px]">
                                                    <label className="text-[10px] uppercase text-yellow-700 font-bold mb-1 block">
                                                        Invoice Number <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control text-sm font-semibold text-yellow-900 bg-white"
                                                        placeholder="e.g. INV-2024-00123"
                                                        value={inv.invoiceNumber}
                                                        onChange={e => handleInvoiceMeta(inv.id, 'invoiceNumber', e.target.value)}
                                                    />
                                                </div>

                                                {/* Invoice Date */}
                                                <div className="min-w-[155px]">
                                                    <label className="text-[10px] uppercase text-yellow-700 font-bold mb-1 block">
                                                        Invoice Date <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="form-control text-sm font-semibold text-yellow-900 bg-white"
                                                        value={inv.invoiceDate}
                                                        onChange={e => handleInvoiceMeta(inv.id, 'invoiceDate', e.target.value)}
                                                    />
                                                </div>

                                                {/* Sender Name */}
                                                <div className="flex-1 min-w-[200px]">
                                                    <label className="text-[10px] uppercase text-yellow-700 font-bold mb-1 block">
                                                        Sender Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control text-sm font-semibold text-yellow-900 bg-white"

                                                        value={inv.senderName}
                                                        onChange={e => handleInvoiceMeta(inv.id, 'senderName', e.target.value)}
                                                    />
                                                </div>

                                                {/* Sender GSTIN */}
                                                <div className="min-w-[180px]">
                                                    <label className="text-[10px] uppercase text-yellow-700 font-bold mb-1 block">
                                                        Sender GSTIN <span className="text-yellow-500 font-normal">(optional)</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        maxLength={15}
                                                        className="form-control text-sm font-mono uppercase text-yellow-900 bg-white"

                                                        value={inv.senderGstin}
                                                        onChange={e => handleInvoiceMeta(inv.id, 'senderGstin', e.target.value.toUpperCase())}
                                                    />
                                                </div>

                                                {/* ── Transporter Details — end of the row (full width below) */}

                                                {/* Received / Batch Date */}
                                                <div className="min-w-[155px]">
                                                    <label className="text-[10px] uppercase text-blue-700 font-bold mb-1 block">
                                                        Received / Batch Date <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="form-control text-sm font-semibold text-blue-900 bg-blue-50 border-blue-200"
                                                        value={inv.receivedDate}
                                                        onChange={e => handleInvoiceMeta(inv.id, 'receivedDate', e.target.value)}
                                                    />
                                                </div>

                                                {/* Invoice Document (PDF) */}
                                                <div className="min-w-[220px] flex-1">
                                                    <label className="text-[10px] uppercase text-purple-700 font-bold mb-1 block">
                                                        <FontAwesomeIcon icon={faFilePdf} className="mr-1 text-red-500" />
                                                        Invoice Document (PDF)
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="file"
                                                            accept="application/pdf"
                                                            id={`invoiceDoc_${invIdx}`}
                                                            className="absolute inset-0 opacity-0 w-full cursor-pointer"
                                                            onChange={e => handleFileChange(inv.id, e.target.files[0] || null)}
                                                        />
                                                        <div className={`form-control text-sm flex items-center gap-2 cursor-pointer ${inv.invoiceDocument
                                                            ? 'bg-green-50 border-green-300 text-green-800'
                                                            : 'bg-white text-gray-400'
                                                            }`}>
                                                            <FontAwesomeIcon
                                                                icon={inv.invoiceDocument ? faFilePdf : faUpload}
                                                                className={inv.invoiceDocument ? 'text-red-500' : 'text-gray-400'}
                                                            />
                                                            <span className="truncate">
                                                                {inv.invoiceDocument
                                                                    ? inv.invoiceDocument.name
                                                                    : 'Click to upload PDF…'
                                                                }
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {inv.invoiceDocument && (
                                                        <button
                                                            type="button"
                                                            className="text-[10px] text-red-500 mt-1 hover:underline"
                                                            onClick={() => handleFileChange(inv.id, null)}
                                                        >
                                                            Remove file
                                                        </button>
                                                    )}
                                                </div>

                                            </div>

                                            {/* ── Transporter Details sub-section ──────────────────────── */}
                                            <div className="mt-4 border border-indigo-100 rounded-xl bg-indigo-50 px-4 pt-4 pb-3">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-[11px] uppercase tracking-widest font-extrabold text-indigo-700">
                                                        Transporter Details
                                                        <span className="ml-2 text-indigo-400 font-normal normal-case tracking-normal text-[10px]">(optional — add one row per transporter)</span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => addTransporter(inv.id)}
                                                        className="flex items-center gap-1.5 text-xs font-bold text-indigo-700 hover:text-white hover:bg-indigo-600 border border-indigo-300 px-3 py-1 rounded-lg transition-colors"
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} /> Add Transporter
                                                    </button>
                                                </div>

                                                {inv.transporterDetails.length === 0 && (
                                                    <p className="text-xs text-indigo-400 italic mb-2">No transporter added yet. Click "Add Transporter" to record freight invoice details.</p>
                                                )}

                                                {inv.transporterDetails.map((tp, tpIdx) => {
                                                    const hasGstin = tp.gstin?.trim().length > 0;
                                                    return (
                                                        <div key={tp.id} className="bg-white border border-indigo-200 rounded-xl p-4 mb-3 relative">
                                                            {/* Row header */}
                                                            <div className="flex items-center justify-between mb-3">
                                                                <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-600">Transporter {tpIdx + 1}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeTransporter(inv.id, tp.id)}
                                                                    className="text-[10px] text-red-400 hover:text-red-600 hover:underline font-bold"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} className="mr-1" />Remove
                                                                </button>
                                                            </div>

                                                            {/* Row 1: GSTIN · Name · Invoice No · Invoice Date */}
                                                            <div className="flex flex-wrap gap-3 mb-3">
                                                                {/* GSTIN */}
                                                                <div className="min-w-[180px] flex-1">
                                                                    <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">GSTIN <span className="text-indigo-300 font-normal">(optional)</span></label>
                                                                    <input
                                                                        type="text"
                                                                        maxLength={15}
                                                                        className="form-control text-sm font-mono uppercase"
                                                                        placeholder="e.g. 27AABCU9603R1ZX"
                                                                        value={tp.gstin}
                                                                        onChange={e => handleTransporterField(inv.id, tp.id, 'gstin', e.target.value)}
                                                                    />
                                                                </div>
                                                                {/* Transporter Name */}
                                                                <div className="min-w-[180px] flex-1">
                                                                    <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">Transporter Name <span className="text-red-500">*</span></label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control text-sm"
                                                                        placeholder="e.g. Blue Dart Express"
                                                                        value={tp.transporterName}
                                                                        onChange={e => handleTransporterField(inv.id, tp.id, 'transporterName', e.target.value)}
                                                                    />
                                                                </div>
                                                                {/* Invoice No */}
                                                                <div className="min-w-[150px] flex-1">
                                                                    <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">Invoice No <span className="text-red-500">*</span></label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control text-sm font-mono"
                                                                        placeholder="e.g. BD-INV-00456"
                                                                        value={tp.invoiceNo}
                                                                        onChange={e => handleTransporterField(inv.id, tp.id, 'invoiceNo', e.target.value)}
                                                                    />
                                                                </div>
                                                                {/* Invoice Date */}
                                                                <div className="min-w-[150px]">
                                                                    <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">Invoice Date <span className="text-red-500">*</span></label>
                                                                    <input
                                                                        type="date"
                                                                        className="form-control text-sm"
                                                                        value={tp.invoiceDate}
                                                                        onChange={e => handleTransporterField(inv.id, tp.id, 'invoiceDate', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Row 2: Taxable Value · (GST fields — only if GSTIN present) · Total */}
                                                            <div className="flex flex-wrap gap-3 items-start">
                                                                {/* Taxable Value */}
                                                                <div className="min-w-[130px]">
                                                                    <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">Taxable Value (₹) <span className="text-red-500">*</span></label>
                                                                    <input
                                                                        type="number" min="0" step="0.01"
                                                                        className="form-control text-sm"
                                                                        placeholder="0.00"
                                                                        value={tp.taxableValue}
                                                                        onChange={e => handleTransporterField(inv.id, tp.id, 'taxableValue', e.target.value)}
                                                                        onWheel={e => e.target.blur()}
                                                                    />
                                                                </div>

                                                                {/* GST fields — conditional on GSTIN being filled */}
                                                                {hasGstin && (
                                                                    <>
                                                                        <div className="min-w-[100px]">
                                                                            <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">GST Slab (%) <span className="text-red-500">*</span></label>
                                                                            <input
                                                                                type="number" min="0" max="100" step="0.01"
                                                                                className="form-control text-sm"
                                                                                placeholder="e.g. 18"
                                                                                value={tp.gstSlab}
                                                                                onChange={e => handleTransporterField(inv.id, tp.id, 'gstSlab', e.target.value)}
                                                                                onWheel={e => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                        <div className="min-w-[110px]">
                                                                            <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">IGST (₹) <span className="text-red-500">*</span></label>
                                                                            <input
                                                                                type="number" min="0" step="0.01"
                                                                                className="form-control text-sm"
                                                                                placeholder="0.00"
                                                                                value={tp.integratedTax}
                                                                                onChange={e => handleTransporterField(inv.id, tp.id, 'integratedTax', e.target.value)}
                                                                                onWheel={e => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                        <div className="min-w-[110px]">
                                                                            <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">CGST (₹) <span className="text-red-500">*</span></label>
                                                                            <input
                                                                                type="number" min="0" step="0.01"
                                                                                className="form-control text-sm"
                                                                                placeholder="0.00"
                                                                                value={tp.centralTax}
                                                                                onChange={e => handleTransporterField(inv.id, tp.id, 'centralTax', e.target.value)}
                                                                                onWheel={e => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                        <div className="min-w-[110px]">
                                                                            <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">SGST (₹) <span className="text-red-500">*</span></label>
                                                                            <input
                                                                                type="number" min="0" step="0.01"
                                                                                className="form-control text-sm"
                                                                                placeholder="0.00"
                                                                                value={tp.stateTax}
                                                                                onChange={e => handleTransporterField(inv.id, tp.id, 'stateTax', e.target.value)}
                                                                                onWheel={e => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )}

                                                                {/* Total Invoice Value */}
                                                                <div className="min-w-[140px]">
                                                                    <label className="text-[10px] uppercase text-indigo-600 font-bold mb-1 block">Total Invoice Value (₹) <span className="text-red-500">*</span></label>
                                                                    <input
                                                                        type="number" min="0" step="0.01"
                                                                        className={`form-control text-sm font-bold ${hasGstin
                                                                                ? 'bg-white'
                                                                                : 'bg-indigo-50 border-indigo-200 text-indigo-800'
                                                                            }`}
                                                                        placeholder="0.00"
                                                                        readOnly={!hasGstin}
                                                                        value={tp.totalInvoiceValue}
                                                                        onChange={e => handleTransporterField(inv.id, tp.id, 'totalInvoiceValue', e.target.value)}
                                                                        onWheel={e => e.target.blur()}
                                                                    />
                                                                    {!hasGstin && (
                                                                        <p className="text-[9px] text-indigo-400 mt-0.5">Auto-filled from Taxable Value (no GSTIN)</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Line-items table for this invoice */}
                                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                                            <table className="w-full text-left align-middle">
                                                <thead className="bg-gray-800 text-[11px] uppercase tracking-widest text-gray-400 font-bold">
                                                    <tr>
                                                        <th className="p-4">#</th>
                                                        <th className="p-4">Model</th>
                                                        <th className="p-4">PO Qty</th>
                                                        <th className="p-4 text-center">Inspected / Approved</th>
                                                        <th className="p-4 text-center">Rejected (Wrong / Damaged / Short)</th>
                                                        <th className="p-4 w-[260px]">Rejection Reason</th>
                                                        <th className="p-4 text-center">Batch Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {po?.lineItems?.map((item, idx) => {
                                                        const data = inv.inspectionData[item._id] || {
                                                            inspectedQuantity: 0,
                                                            approvedQuantity: 0,
                                                            wrongQuantity: 0,
                                                            damagedQuantity: 0,
                                                            shortSupply: 0,
                                                            rejectionReason: ''
                                                        };
                                                        const wrongQty = Number(data.wrongQuantity) || 0;
                                                        const damagedQty = Number(data.damagedQuantity) || 0;
                                                        const shortQty = Number(data.shortSupply) || 0;
                                                        const rejQty = wrongQty + damagedQty + shortQty;
                                                        const appQty = Number(data.approvedQuantity) || 0;
                                                        const hasItemRejections = rejQty > 0;
                                                        const isFullyRejected = rejQty > 0 && appQty === 0;
                                                        const crossTotal = totalInspectedForItem(item._id);
                                                        const isOverPO = crossTotal > item.quantity;

                                                        return (
                                                            <tr
                                                                key={item._id}
                                                                className={`transition-colors ${isOverPO
                                                                    ? 'bg-red-50/70'
                                                                    : isFullyRejected
                                                                        ? 'bg-red-50/40 hover:bg-red-50'
                                                                        : hasItemRejections
                                                                            ? 'bg-orange-50/30 hover:bg-orange-50'
                                                                            : 'hover:bg-green-50'
                                                                    }`}
                                                            >
                                                                <td className="p-4 font-semibold text-gray-400 text-sm">{idx + 1}</td>

                                                                <td className="p-4">
                                                                    <div className="font-extrabold text-gray-900 text-sm mb-0.5">
                                                                        {item.model?.modelName || '—'}
                                                                    </div>
                                                                    <div className="text-xs text-gray-400 font-mono">
                                                                        {item.model?.partCode}
                                                                        <span className="ml-2 bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-bold uppercase">
                                                                            {item.model?.uom}
                                                                        </span>
                                                                    </div>
                                                                    {isOverPO && (
                                                                        <div className="text-[10px] text-red-600 font-bold mt-1 flex items-center gap-1">
                                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                            Total across invoices ({crossTotal}) exceeds PO qty ({item.quantity})
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                <td className="p-4 font-extrabold text-2xl text-gray-800">
                                                                    {item.quantity}
                                                                </td>

                                                                <td className="p-4">
                                                                    <div className="flex flex-wrap gap-2 justify-center items-start min-w-[200px]">
                                                                        {/* Inspected */}
                                                                        <div className="flex flex-col items-center">
                                                                            <label className="text-[10px] uppercase text-gray-500 font-bold mb-1">Insp</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                step="1"
                                                                                className={`form-control text-sm font-bold w-16 md:w-20 text-center ${isOverPO ? 'border-red-400 bg-red-50' : ''}`}
                                                                                value={data.inspectedQuantity === undefined ? '' : data.inspectedQuantity}
                                                                                onChange={e => handleFieldChange(inv.id, item._id, 'inspectedQuantity', e.target.value)}
                                                                                onWheel={(e) => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                        <div className="text-gray-300 font-bold text-lg mt-5 hidden lg:block">=</div>
                                                                        {/* Approved */}
                                                                        <div className="flex flex-col items-center">
                                                                            <label className="text-[10px] uppercase text-green-600 font-bold mb-1">Appr</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                className="form-control text-sm font-bold w-16 md:w-20 text-center border-green-200 text-green-700 bg-green-50"
                                                                                value={data.approvedQuantity === undefined ? '' : data.approvedQuantity}
                                                                                onChange={e => handleFieldChange(inv.id, item._id, 'approvedQuantity', e.target.value)}
                                                                                onWheel={(e) => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* Rejection qty columns */}
                                                                <td className="p-4">
                                                                    <div className="flex flex-wrap gap-2 justify-center items-start min-w-[240px]">
                                                                        {/* Wrong */}
                                                                        <div className="flex flex-col items-center">
                                                                            <label className="text-[10px] uppercase text-orange-600 font-bold mb-1">Wrong</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                className="form-control text-sm font-bold w-16 md:w-20 text-center border-orange-200 text-orange-700 bg-orange-50"
                                                                                value={data.wrongQuantity === undefined ? '' : data.wrongQuantity}
                                                                                onChange={e => handleFieldChange(inv.id, item._id, 'wrongQuantity', e.target.value)}
                                                                                onWheel={(e) => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                        {/* Damaged */}
                                                                        <div className="flex flex-col items-center">
                                                                            <label className="text-[10px] uppercase text-red-600 font-bold mb-1">Damaged</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                className="form-control text-sm font-bold w-16 md:w-20 text-center border-red-200 text-red-700 bg-red-50"
                                                                                value={data.damagedQuantity === undefined ? '' : data.damagedQuantity}
                                                                                onChange={e => handleFieldChange(inv.id, item._id, 'damagedQuantity', e.target.value)}
                                                                                onWheel={(e) => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                        {/* Short Supply */}
                                                                        <div className="flex flex-col items-center">
                                                                            <label className="text-[10px] uppercase text-purple-600 font-bold mb-1">Short</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                className="form-control text-sm font-bold w-16 md:w-20 text-center border-purple-200 text-purple-700 bg-purple-50"
                                                                                value={data.shortSupply === undefined ? '' : data.shortSupply}
                                                                                onChange={e => handleFieldChange(inv.id, item._id, 'shortSupply', e.target.value)}
                                                                                onWheel={(e) => e.target.blur()}
                                                                            />
                                                                        </div>
                                                                        {rejQty > 0 && (
                                                                            <div className="flex flex-col items-center ml-1">
                                                                                <label className="text-[10px] uppercase text-gray-400 font-bold mb-1">Total</label>
                                                                                <div className="w-14 h-[38px] flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-sm font-extrabold text-gray-600">
                                                                                    {rejQty}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                <td className="p-4">
                                                                    {hasItemRejections ? (
                                                                        <div className="bg-red-50 p-2 rounded-lg border border-red-100">
                                                                            <label className="text-[10px] uppercase tracking-widest text-red-700 font-bold block mb-1">
                                                                                Reason <span className="text-red-400">*</span>
                                                                            </label>
                                                                            <textarea
                                                                                rows="2"
                                                                                className="form-control text-xs"
                                                                                placeholder="Describe why materials are rejected…"
                                                                                value={data.rejectionReason || ''}
                                                                                onChange={e => handleFieldChange(inv.id, item._id, 'rejectionReason', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-gray-400 text-xs italic flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                                                                            <FontAwesomeIcon icon={faCheck} className="mr-2 text-green-500" />
                                                                            All items approved
                                                                        </div>
                                                                    )}
                                                                </td>

                                                                <td className="p-4 text-center">
                                                                    {isFullyRejected ? (
                                                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-red-700 bg-red-100 px-2.5 py-1 rounded-full">
                                                                            <FontAwesomeIcon icon={faWarehouse} /> Batch — Rejected
                                                                        </span>
                                                                    ) : hasItemRejections ? (
                                                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-orange-700 bg-orange-100 px-2.5 py-1 rounded-full">
                                                                            <FontAwesomeIcon icon={faWarehouse} /> Batch — Partial
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                                                                            <FontAwesomeIcon icon={faBoxes} /> Batch — Approved
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Per-invoice rejection notice */}
                                        {invHasRejections && (
                                            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 mt-4 text-sm text-orange-800">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 flex-shrink-0 text-orange-500" />
                                                <p className="mb-0 text-xs">
                                                    <strong>Rejected stock</strong> for this delivery will remain in store, marked separately.
                                                    A GSPL admin will decide the next step. The supplier will issue a <strong>Credit Note</strong>;
                                                    use <strong>"Create Debit Note"</strong> on the PO to raise GSPL's side.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Add Invoice button */}
                <button
                    type="button"
                    onClick={addInvoice}
                    className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50 text-blue-600 font-bold uppercase tracking-widest text-sm py-4 rounded-2xl transition-all mb-6"
                >
                    <FontAwesomeIcon icon={faPlus} /> Add Another Invoice / Delivery
                </button>

                {/* Inventory batch info */}
                <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-5 text-sm text-green-800">
                    <FontAwesomeIcon icon={faBoxes} className="mt-0.5 flex-shrink-0 text-green-500" />
                    <p className="mb-0">
                        <strong>A separate inventory batch will be created per invoice / delivery.</strong> Rejected quantities
                        will be marked clearly in each batch so GSPL admin can take appropriate action.
                    </p>
                </div>

                {/* Error banner */}
                {submitError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-red-800 text-sm font-semibold">
                        <FontAwesomeIcon icon={faTimes} className="text-red-500" /> {submitError}
                    </div>
                )}

                {/* Success + per-invoice Tally sync status */}
                {submitSuccess && (
                    <div className="mb-4 space-y-3">
                        {/* Main success message */}
                        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-green-800 text-sm font-semibold">
                            <FontAwesomeIcon icon={faCheck} className="text-green-500" /> {submitSuccess}
                        </div>

                        {/* Per-invoice Tally sync cards */}
                        {createdInspections.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                <div className="bg-gray-900 px-4 py-2.5 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faFileInvoice} className="text-yellow-400 text-xs" />
                                    <span className="text-white text-xs font-bold uppercase tracking-widest">Tally Sync Status</span>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {createdInspections.map(insp => {
                                        const isPending = insp.tallyPurchaseSyncPending !== false;
                                        const isSyncing = syncingTally[insp._id];
                                        return (
                                            <div key={insp._id} className="flex items-center justify-between px-4 py-3 gap-3">
                                                <div>
                                                    <span className="font-bold text-gray-800 text-sm">{insp.invoiceNumber}</span>
                                                    <span className="ml-2 text-xs text-gray-400 font-mono">{insp._id?.slice(-6)}</span>
                                                </div>
                                                {isPending ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className="flex items-center gap-1.5 bg-orange-50 border border-orange-300 text-orange-700 text-[10px] font-bold px-2.5 py-1 rounded-full">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-[9px]" />
                                                            Not Synced
                                                        </span>
                                                        <button
                                                            onClick={() => handleSyncToTally(insp._id)}
                                                            disabled={isSyncing}
                                                            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all active:scale-95"
                                                        >
                                                            {isSyncing
                                                                ? <Spinner size="sm" className="text-white" style={{ width: '10px', height: '10px' }} />
                                                                : <FontAwesomeIcon icon={faFileInvoice} className="text-[9px]" />
                                                            }
                                                            {isSyncing ? 'Syncing…' : 'Sync to Tally'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="flex items-center gap-1.5 bg-green-50 border border-green-300 text-green-700 text-[10px] font-bold px-2.5 py-1 rounded-full">
                                                        <FontAwesomeIcon icon={faCheck} className="text-[9px]" />
                                                        Synced to Tally
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Navigation button shown after sync statuses are visible */}
                                <div className="bg-gray-50 border-t border-gray-100 px-4 py-3 flex justify-end">
                                    <button
                                        onClick={() => navigate('/purchase/view_po')}
                                        className="flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} /> Back to Purchase Orders
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Submit button — hidden once inspection is saved */}
                {!submitSuccess && (
                    <div className="flex justify-end">
                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-8 py-3 rounded-xl font-extrabold uppercase tracking-widest text-sm shadow-md transition-all flex items-center gap-3"
                        >
                            {submitting ? <Spinner size="sm" className="text-white" /> : <FontAwesomeIcon icon={faSave} />}
                            Save Inspection Results
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default MaterialInspection;
